import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Rxjs
import { Observable , of} from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
// Environment
import { environment } from 'src/environments/environment';
// Model
import { MessageType } from '../../components/messages/message-handler/message-handler.component';
import { MessageHandlerService } from '../../components/messages/message-handler/message-handler.service';
import { SaveType } from '../../core/models/enumerations/save-type';
// Interface
import { TranslateService } from '@ngx-translate/core';
import { LoadWheelService } from '../../components/load-wheel/load-wheel.service';

import { EntityName } from '../../core/models/enumerations/entity-name';
import { PlantLTSASearch, PlantLTSASearchAdapter } from '../../core/models/plant-ltsa-search';
import { PlantsLTSADisplay } from '../../core/models/plant-ltsa/display/plants-ltsa-display';
import { SpinnerProcess } from '../../core/models/spinner-process';
import { RecordLockingFlow } from '../../core/record-locking/record-locking-flow';
import { EntityCommonService } from '../../core/services/entity-common.service';
import { LockSameUserMessageService, LockSameUserResult } from '../../core/services/lock-same-user-message.service';
import { TelemetryService } from '../../core/services/telemetry.service';
import { EntityUtilsService } from '../../core/utils/entity-utils.service';

@Injectable({
    providedIn: 'root'
})
export class PlantLtsaSectionService {

    constructor(
        private readonly adapter: PlantLTSASearchAdapter,
        private readonly recordLockingFlow: RecordLockingFlow,
        private readonly http: HttpClient,
        private readonly messageHandlerService: MessageHandlerService,
        private readonly loadWheelService: LoadWheelService,
        private readonly entityCommonService: EntityCommonService,
        private readonly translate: TranslateService,
        private readonly plantLTSASearchAdapter: PlantLTSASearchAdapter,
        private readonly entityUtilsService: EntityUtilsService,
        private readonly lockSameUserMessageService: LockSameUserMessageService,
        private readonly telemetry: TelemetryService,
    ) {
    }

    getPlantLtsaDetailsById(ltsaId: string, isReload: boolean = false, quickAccess: boolean = false): Promise<PlantsLTSADisplay> {
        let mainLockID: number = 0;

        if (isReload) {
            mainLockID = this.recordLockingFlow.getLockID(EntityName.PLANT_LTSA, ltsaId);
        }

        return new Promise<PlantsLTSADisplay>((resolve) => {
            this.lockSameUserMessageService.validateEntityIsLockedByUser(this.recordLockingFlow, EntityName.PLANT_LTSA, ltsaId, mainLockID, quickAccess).then((lockSameUserResult: LockSameUserResult) => {
                if(lockSameUserResult.openEntity) {
                    this.getEntityDetailsById(ltsaId, mainLockID, false).subscribe((entityDisplay: PlantsLTSADisplay) => {
                        resolve(entityDisplay);
                    });
                } else {
                    resolve(PlantsLTSADisplay.BuildPlantLTSADisplay({}));
                }
            });
        });
    }

    private getEntityDetailsById(ltsaId: string, mainLockID: number, onlyForRead: boolean): Observable<PlantsLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.plantLTSA") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<PlantsLTSADisplay>(`${environment.apiUrl}plantLtsa/${ltsaId}/${mainLockID}/${onlyForRead}`)
            .pipe(
                map((data: any) => {
                    let ltsaDisplay = PlantsLTSADisplay.BuildPlantLTSADisplay({});
                    if (data.response) {
                        ltsaDisplay = PlantsLTSADisplay.BuildPlantLTSADisplay(data.response);

                        if (ltsaDisplay.plantLtsaId) {
                            if (ltsaDisplay.hasDiffUserLockError()) {
                                this.messageHandlerService.show(ltsaDisplay.lockError, MessageType.INFO);
                            }

                            // Set the locking info into lock global list.
                            this.recordLockingFlow.setLockItem(EntityName.PLANT_LTSA, ltsaDisplay.plantLtsaId, ltsaDisplay.lockId, ltsaDisplay.lockMode);
                            this.recordLockingFlow.initLockRefreshTimer(EntityName.PLANT_LTSA, ltsaDisplay.plantLtsaId, ltsaDisplay.lockId);
                        } else {
                            this.messageHandlerService.show(ltsaDisplay.lockError, MessageType.ERROR);
                        }
                    } else {
                        const errorType = MessageHandlerService.errorType(data);
                        if (errorType === MessageType.INFO) {
                            this.entityCommonService.sendEntityNotFoundEvent();
                        } else if (errorType !== MessageType.SESSION_INVALID) {
                            this.messageHandlerService.show(MessageHandlerService.errorMessage(data), errorType);
                        }
                    }
                    return ltsaDisplay;
                }),
                catchError((error) => {
                    return of(PlantsLTSADisplay.BuildPlantLTSADisplay({}));
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getLtsaById(ltsaId: number): Observable<PlantLTSASearch> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.plantLTSA") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<PlantLTSASearch>(`${environment.apiUrl}plantLtsa/${ltsaId}`)
            .pipe(
                map((data: any) => {
                    return this.adapter.adapt(data.response[0]);
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    saveLtsaDetails(plantsLTSADisplay: PlantsLTSADisplay): Observable<PlantsLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("saving.ltsa"));
        plantsLTSADisplay = this.entityUtilsService.prepareEntityForSave(plantsLTSADisplay) as PlantsLTSADisplay;
        return this.http.put<PlantsLTSADisplay>(`${environment.apiUrl}plantLtsa`, plantsLTSADisplay)
            .pipe(
                map((data: any) => {
                    if (data.response === null) {
                        if (data.exception !== null) {
                            this.messageHandlerService.showExceptionDialog(data);
                        } else if (data.message !== null) {
                            if (plantsLTSADisplay.saveType === SaveType.TYPE_QC || plantsLTSADisplay.saveType === SaveType.UPDATE) {
                                //When the user attempts to QC a record while the lock was already cleared,
                                //this//the above warning message should still be thrown; however, the record should not reload.
                                if (data.messageType !== MessageType.WARNING.valueOf()) {
                                    this.entityCommonService.sendReloadEvent();
                                }
                            } else {
                                plantsLTSADisplay.intDataDepMigError = data.message;
                            }

                        }
                    } else {
                        const isQcUpdateOrAmend = plantsLTSADisplay.saveType === SaveType.TYPE_QC ||
                            plantsLTSADisplay.saveType === SaveType.AMENDMENT ||
                            plantsLTSADisplay.saveType === SaveType.UPDATE;

                        const afterClearFunc = () => this.entityCommonService.sendPreReleaseEvent();
                        this.recordLockingFlow.clearLockFlow(EntityName.PLANT_LTSA, String(plantsLTSADisplay.plantLtsaId), isQcUpdateOrAmend, afterClearFunc);
                    }
                    return data.response;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    ltsaNextId(): Observable<PlantsLTSADisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.nextId"));
        return this.http.get<any>(`${environment.apiUrl}plantLtsa/nextId`)
            .pipe(
                map((data: any) => {
                    const plantsLTSADisplay = PlantsLTSADisplay.BuildNewPlantLTSA(data.response);
                    if (plantsLTSADisplay?.plantLtsaId) {
                        // Set the locking info into lock global list.
                        this.recordLockingFlow.setLockItem(EntityName.PLANT_LTSA, plantsLTSADisplay.plantLtsaId, -1);
                    }
                    return plantsLTSADisplay;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    searchPlantLTSAs(httpParams: HttpParams): Observable<PlantLTSASearch[]> {
        const searchStart: number = performance.now();
       
        return this.http.get<PlantLTSASearch[]>(`${environment.apiUrl}plantLtsa/searchPlantLTSAs`, { params: httpParams })
            .pipe(
                map((data: any) => {
                    this.entityUtilsService.verifyMaxResult(data);
                    let ltsaList: Array<any> = data.response;

                    if (ltsaList.length > 1) {
                        ltsaList = this.removeDuplicatedLtsas(ltsaList);
                        ltsaList = this.sortLtsaById(ltsaList);
                    }
                    
                    this.telemetry.entitySearchTime("plant LTSA", performance.now() - searchStart);

                    return ltsaList.map(item => this.plantLTSASearchAdapter.adapt(item));
                })
            );
    }

    private removeDuplicatedLtsas(ltsas: Array<any>): Array<any> {
        let uniqueLtsas: Array<any> = [];
        ltsas.forEach(ltsa => {
            if (!uniqueLtsas.some(uniqueLtsa => uniqueLtsa.PLANT_LTSA_ID == ltsa.PLANT_LTSA_ID)) {
                uniqueLtsas.push(ltsa);
            }
        });
        return uniqueLtsas;
    }

    private sortLtsaById(ltsas: Array<any>): Array<any> {
        return ltsas.sort((a, b) => {
            if (a.PLANT_LTSA_ID < b.PLANT_LTSA_ID) return -1;
            if (a.PLANT_LTSA_ID > b.PLANT_LTSA_ID) return 1;
            return 0;
        });
    }

}
